import { FIRST_PAGE, LAST_PAGE, isPageNumber, type PageSummary, type Subpage } from '@textbox/core';
import type { PageSource } from './source.ts';

export type Mode = 'idle' | 'entering' | 'searching';

export interface NavState {
  mode: Mode;
  /** Page whose data is on screen (null before the first page arrives). */
  shown: number | null;
  /** Subpage on screen, 1-based. */
  sub: number;
  subCount: number;
  /** Digits typed so far. */
  entry: string;
  /** Number rolling in the header while searching. */
  searchNum: number;
  /** The page being searched for. */
  target: number | null;
  notInService: boolean;
  hold: boolean;
  reveal: boolean;
  mix: boolean;
}

export interface NavConfig {
  searchMinMs: number;
  searchMaxMs: number;
  notFoundMs: number;
  rollMs: number;
  rotateMs: number;
  pollMs: number;
  /** Don't animate the rolling number (prefers-reduced-motion). */
  reducedMotion: boolean;
  random: () => number;
}

export const DEFAULT_CONFIG: NavConfig = {
  searchMinMs: 600,
  searchMaxMs: 1500,
  notFoundMs: 3200,
  rollMs: 90,
  rotateMs: 20_000,
  pollMs: 30_000,
  reducedMotion: false,
  random: Math.random,
};

export type FastextKey = 'red' | 'green' | 'yellow' | 'blue';

export interface NavListener {
  (state: NavState, subpage: Subpage | null, reason: 'page' | 'sub' | 'entry' | 'search' | 'toggle' | 'refresh'): void;
}

/**
 * The set's navigation model (brief §4.2): three-digit entry, the
 * searching state with a rolling header, Fastext, subpage rotation with
 * hold, reveal and mix. Pure of DOM — the shell subscribes to changes.
 */
export class Navigator {
  readonly state: NavState = {
    mode: 'idle', shown: null, sub: 1, subCount: 1, entry: '', searchNum: FIRST_PAGE, target: null,
    notInService: false, hold: false, reveal: false, mix: false,
  };
  current: Subpage | null = null;
  summaries = new Map<number, PageSummary>();

  private listeners: NavListener[] = [];
  private searchToken = 0;
  private rollTimer: ReturnType<typeof setInterval> | null = null;
  private rotateTimer: ReturnType<typeof setInterval> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private onNavigate: ((page: number, sub: number, replace: boolean) => void) | null = null;

  private source: PageSource;
  readonly config: NavConfig;

  constructor(source: PageSource, config: NavConfig = DEFAULT_CONFIG) {
    this.source = source;
    this.config = config;
  }

  subscribe(fn: NavListener): () => void {
    this.listeners.push(fn);
    return () => { this.listeners = this.listeners.filter((l) => l !== fn); };
  }

  /** Called whenever the page/subpage on screen changes (for URL hash sync). */
  setNavigateHandler(fn: (page: number, sub: number, replace: boolean) => void): void {
    this.onNavigate = fn;
  }

  private emit(reason: Parameters<NavListener>[2]): void {
    for (const l of this.listeners) l(this.state, this.current, reason);
  }

  private unsubscribe: (() => void) | null = null;

  async start(initialPage = FIRST_PAGE, initialSub = 1): Promise<void> {
    void this.refreshIndex();
    if (this.config.pollMs > 0) this.pollTimer = setInterval(() => void this.poll(), this.config.pollMs);
    this.unsubscribe = this.source.subscribe?.((e) => void this.onPageEvent(e)) ?? null;
    await this.goto(initialPage, initialSub);
  }

  destroy(): void {
    this.searchToken++;
    this.stopRoll();
    this.stopRotation();
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.unsubscribe?.();
  }

  /** A page changed upstream: refresh it in place if it's on screen. */
  private async onPageEvent(e: { page: number; sub: number; type: 'update' | 'remove' }): Promise<void> {
    void this.refreshIndex();
    const cur = this.current;
    if (!cur || cur.page !== e.page || this.state.mode !== 'idle') return;
    if (e.type === 'remove' && e.sub === cur.sub) {
      const first = await this.source.subpage(cur.page, 1).catch(() => null);
      if (this.current === cur) {
        if (first) this.show(first, true, 'refresh');
        else { this.current = null; this.state.notInService = true; this.state.subCount = 1; this.state.sub = 1; this.emit('page'); }
      }
      return;
    }
    if (e.sub !== cur.sub) return;
    const sp = await this.source.subpage(cur.page, cur.sub).catch(() => null);
    if (sp && this.current === cur) this.show(sp, true, 'refresh');
  }

  // ---- number entry -------------------------------------------------

  digit(d: string): void {
    if (!/^[0-9]$/.test(d)) return;
    const s = this.state;
    if (s.mode === 'searching') this.cancelSearch();
    if (s.entry === '' && (d === '0' || d === '9')) return; // 0xx and 9xx are rejected
    s.mode = 'entering';
    s.entry += d;
    if (s.entry.length === 3) {
      const n = Number(s.entry);
      s.entry = '';
      void this.goto(n);
      return;
    }
    this.emit('entry');
  }

  escape(): void {
    const s = this.state;
    if (s.mode === 'searching') { this.cancelSearch(); return; }
    if (s.mode === 'entering') { s.entry = ''; s.mode = 'idle'; this.emit('entry'); }
  }

  backspace(): void {
    const s = this.state;
    if (s.mode !== 'entering') return;
    s.entry = s.entry.slice(0, -1);
    if (s.entry === '') s.mode = 'idle';
    this.emit('entry');
  }

  // ---- navigation ---------------------------------------------------

  fastext(key: FastextKey): void {
    const target = this.current?.fastext[key];
    if (target != null) void this.goto(target);
  }

  index(): void {
    void this.goto(this.current?.fastext.index ?? FIRST_PAGE);
  }

  /**
   * Fetch a page through the searching state. The previous page stays on
   * screen while the header rolls; a deliberate delay applies even when
   * the page is cached. Unknown pages search for ~3 s then report
   * "page not in service".
   */
  async goto(page: number, sub = 1): Promise<boolean> {
    if (!isPageNumber(page)) return false;
    const s = this.state;
    const token = ++this.searchToken;
    this.stopRotation();
    this.stopRoll();
    s.mode = 'searching';
    s.entry = '';
    s.target = page;
    s.searchNum = page;
    s.reveal = false;
    s.hold = false;
    this.emit('search');

    const started = Date.now();
    const delay = this.config.searchMinMs + this.config.random() * (this.config.searchMaxMs - this.config.searchMinMs);
    if (!this.config.reducedMotion && this.config.rollMs > 0) {
      this.rollTimer = setInterval(() => {
        s.searchNum = FIRST_PAGE + ((s.searchNum - FIRST_PAGE + 37) % (LAST_PAGE - FIRST_PAGE + 1));
        this.emit('search');
      }, this.config.rollMs);
    }

    let sp: Subpage | null = null;
    try {
      sp = await this.source.subpage(page, sub);
      if (sp === null && sub !== 1) sp = await this.source.subpage(page, 1);
    } catch {
      sp = null;
    }
    if (token !== this.searchToken) return false;

    const wait = (sp ? delay : this.config.notFoundMs) - (Date.now() - started);
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    if (token !== this.searchToken) return false;

    this.stopRoll();
    s.mode = 'idle';
    s.target = null;
    if (sp) {
      this.show(sp, false);
    } else {
      this.current = null;
      s.shown = page;
      s.sub = 1;
      s.subCount = 1;
      s.notInService = true;
      this.onNavigate?.(page, 1, false);
      this.emit('page');
    }
    return sp !== null;
  }

  private cancelSearch(): void {
    this.searchToken++;
    this.stopRoll();
    this.state.mode = 'idle';
    this.state.target = null;
    this.emit('search');
    this.startRotation();
  }

  private show(sp: Subpage, replaceHistory: boolean, reason: 'page' | 'sub' | 'refresh' = 'page'): void {
    const s = this.state;
    this.current = sp;
    s.shown = sp.page;
    s.sub = sp.sub;
    s.subCount = sp.subCount;
    s.notInService = false;
    if (reason !== 'refresh') this.onNavigate?.(sp.page, sp.sub, replaceHistory);
    this.emit(reason);
    this.startRotation();
    if (sp.subCount > 1) void this.source.subpage(sp.page, (sp.sub % sp.subCount) + 1); // prefetch next
  }

  // ---- subpages -----------------------------------------------------

  async stepSub(direction: 1 | -1): Promise<void> {
    const cur = this.current;
    if (!cur || cur.subCount < 2 || this.state.mode === 'searching') return;
    const next = ((cur.sub - 1 + direction + cur.subCount) % cur.subCount) + 1;
    this.state.hold = true;
    const sp = await this.source.subpage(cur.page, next).catch(() => null);
    if (sp && this.current === cur) this.show(sp, true, 'sub');
    else this.emit('toggle');
  }

  private startRotation(): void {
    this.stopRotation();
    const cur = this.current;
    if (!cur || cur.subCount < 2) return;
    const ms = cur.meta.cycleSeconds ? cur.meta.cycleSeconds * 1000 : this.config.rotateMs;
    this.rotateTimer = setInterval(() => void this.rotate(), Math.max(1000, this.config.rotateMs === DEFAULT_CONFIG.rotateMs ? ms : this.config.rotateMs));
  }

  private async rotate(): Promise<void> {
    const cur = this.current;
    const s = this.state;
    if (!cur || cur.subCount < 2 || s.hold || s.mode !== 'idle') return;
    const next = (cur.sub % cur.subCount) + 1;
    const sp = await this.source.subpage(cur.page, next).catch(() => null);
    if (sp && this.current === cur && !s.hold && s.mode === 'idle') this.show(sp, true, 'sub');
  }

  private stopRotation(): void {
    if (this.rotateTimer) clearInterval(this.rotateTimer);
    this.rotateTimer = null;
  }

  private stopRoll(): void {
    if (this.rollTimer) clearInterval(this.rollTimer);
    this.rollTimer = null;
  }

  // ---- toggles ------------------------------------------------------

  toggleHold(): void { this.state.hold = !this.state.hold; this.emit('toggle'); }
  toggleReveal(): void { this.state.reveal = !this.state.reveal; this.emit('toggle'); }
  toggleMix(): void { this.state.mix = !this.state.mix; this.emit('toggle'); }

  // ---- live updates -------------------------------------------------

  private async refreshIndex(): Promise<void> {
    try {
      const idx = await this.source.index();
      this.summaries = new Map(idx.pages.map((p) => [p.page, p]));
    } catch {
      /* index is optional: captions just won't have titles */
    }
  }

  /** Re-read the index; if the page on screen changed upstream, refetch it in place. */
  async poll(): Promise<void> {
    const before = this.current ? this.summaries.get(this.current.page)?.updatedAt : undefined;
    this.source.invalidate();
    await this.refreshIndex();
    const cur = this.current;
    if (!cur || this.state.mode !== 'idle') return;
    const after = this.summaries.get(cur.page)?.updatedAt;
    if (after === before) return;
    const sp = await this.source.subpage(cur.page, cur.sub).catch(() => null);
    if (sp && this.current === cur) this.show(sp, true, 'refresh');
  }

  titleOf(page: number): string | undefined {
    return this.summaries.get(page)?.title;
  }
}
